import { PAGE_TRANSITION_LABEL, SPEED_LABEL } from '@/lib/experience-theme';
import { sessionDurationLabel, sessionTime } from '@/lib/session-labels';
import type {
  PageTransition,
  SessionDetail,
  SessionNamedValue,
  SessionStatus,
  SessionTurn,
  SessionWalkPage,
} from '@matter_extractor/shared';

/**
 * What the parcours of §12.2 SAYS, in one pure module beside the components
 * that draw it - the régime of `entretien-labels.ts` two folders away.
 *
 * ## Why these sentences are not written inline
 *
 * Two of them are decisions rather than strings: « quittée par le bouton X » has
 * five shapes and only one of them is the ordinary one, and a turn is tied to a
 * page by the CLOCK rather than by anything a row records. Each is a branch to
 * get wrong, and a branch tested without a DOM is a branch a component test
 * would otherwise have had to assert through a render.
 *
 * ## What is NOT here, and why it is not
 *
 * Three sentences this file used to hold are read by the conversation of §12.3
 * too, so they live in `lib/conversation-model.ts` with the rest of what both
 * screens read: the model a seat answered with (`seatModels`), what closed the
 * conversation (`protocolClosingSentence`), and how a value the person typed is
 * worded (`namedValueLabel`, in `lib/session-labels.ts` with the vocabulary).
 * A second copy of any of them is a second answer to one question, which is
 * precisely how they came to disagree.
 *
 * Nothing here counts. Every figure of this screen comes from
 * `session/figures.ts` of `shared`, which is the one place the arithmetic of the
 * three Sessions screens and of the MCP assistant lives: a second euro would be a
 * second answer.
 */

/* ------------------------------------------------------------ the plurals */

/**
 * « 1 tour » / « 4 tours ». French, so the plural turns at two and not at one -
 * `0 tour` is singular, which is the half of the rule an English-speaking
 * formatter gets wrong.
 */
export function plural(count: number, singular: string, many = `${singular}s`): string {
  return `${count} ${Math.abs(count) >= 2 ? many : singular}`;
}

/* ------------------------------------------------------------- the values */

/** Whether the tick of the maquette is drawn beside it - a checked box only. */
export function isTicked(value: SessionNamedValue['value']): boolean {
  return value === true;
}

/* -------------------------------------------------------------- the pages */

/** How long a page held somebody, or null while it still does. */
export function pageDurationMs(page: SessionWalkPage): number | null {
  if (page.leftAt === null) return null;
  const entered = Date.parse(page.enteredAt);
  const left = Date.parse(page.leftAt);
  if (Number.isNaN(entered) || Number.isNaN(left)) return null;
  return Math.max(0, left - entered);
}

/**
 * « 14:31 → 14:32 · 1 min », and « 14:31 → … » on the page a session is still
 * standing on.
 *
 * The arrow is drawn whatever happens, because the line answers « when » before
 * it answers « how long »: a page with no departure is a page somebody is on,
 * and an hour with nothing after it would read as a missing value.
 */
export function pageWindowLabel(page: SessionWalkPage): string {
  const duration = pageDurationMs(page);
  const left = page.leftAt === null ? '…' : sessionTime(page.leftAt);
  const span = duration === null ? '' : ` · ${sessionDurationLabel(duration)}`;
  return `${sessionTime(page.enteredAt)} → ${left}${span}`;
}

/**
 * The « quittée par » line of §12.2, in its five shapes.
 *
 * `emphasis` is what the maquette sets in bold - the button's own words - and
 * it is kept apart from the sentence rather than interpolated, so the component
 * can draw it as the maquette draws it without parsing its own string back.
 *
 * The order of the branches is the order of certainty: a label is what the
 * person actually pressed, a block id without one is a button whose words were
 * not recorded, and a departure with neither is the automatic duration of §9.2.
 * Only then does a page with no departure at all get its two readings, where
 * the session's status is the only thing that separates « terminée » from
 * « encore là ».
 */
export interface PageExit {
  readonly text: string;
  /** The button's words, already in French quotes; null when there are none. */
  readonly emphasis: string | null;
}

export function pageExit(
  page: SessionWalkPage,
  status: SessionStatus,
  /**
   * The name of the card CONFIRMED on a page of choice of the noyau (noyau.md
   * §9.1, #152): « Choisir ce texte » is the page's way out, and what it
   * confirmed is the half of the exit the button's words do not say.
   */
  confirmed: string | null = null,
): PageExit {
  const label = page.pressedLabel?.trim() ?? '';
  if (label !== '' && confirmed !== null) {
    return { text: `Quittée par « ${label} », carte confirmée :`, emphasis: `« ${confirmed} »` };
  }
  if (label !== '') return { text: 'Quittée par le bouton', emphasis: `« ${label} »` };
  if (page.pressedBlockId !== null) {
    return {
      text: 'Quittée par un bouton dont le libellé n’a pas été enregistré.',
      emphasis: null,
    };
  }
  if (page.leftAt !== null) {
    return { text: 'Quittée toute seule, au bout de sa durée.', emphasis: null };
  }
  if (status === 'finished') {
    return { text: 'Dernière page atteinte : l’atteindre termine la session.', emphasis: null };
  }
  return { text: 'La session ne l’a pas quittée.', emphasis: null };
}

/* -------------------------------------------------------------- the edges */

/** « fondu · normal » - the same reading as the scene of §9.1, word for word. */
export function transitionLabel(transition: PageTransition): string {
  return `${PAGE_TRANSITION_LABEL[transition.animation]} · ${SPEED_LABEL[transition.speed]}`;
}

/**
 * What the edge says when a CLOSED session went from the protocol page to the
 * page of retour (retour-experience.md §6.2, §8, issue #176): the person did
 * not play the pages between - the panel of closure led there directly - so the
 * edge names the way it came where an ordinary one would name a transition,
 * as the edge of the noyau says « calculée en fond ».
 */
export const FEEDBACK_EDGE_NOTE = '« Donner mon avis », depuis le panneau de fermeture';

/**
 * The note of the edge BEFORE the card at `position` of the walk, or null when
 * it is an ordinary edge.
 *
 * Read off the fact the server guarantees rather than off a kind a visit does
 * not carry (`SessionWalkPage` names a page and nothing of what it is): a
 * session `closed` admits ONE advance, from the protocol page to the page of
 * retour (§6.2), so the visit right after the protocol's, on a closed session,
 * IS the feedback page. A session of any other status reached the pages
 * between by playing them, and its edge stays ordinary.
 */
export function feedbackEdgeNote(
  status: SessionStatus,
  protocolAt: number | null,
  position: number,
): string | null {
  if (status !== 'closed' || protocolAt === null) return null;
  return position === protocolAt + 1 ? FEEDBACK_EDGE_NOTE : null;
}

/* ----------------------------------------------------------- the protocol */

/**
 * Which visit of the walk a turn was played on.
 *
 * A turn records no page: it records a step (§8.7). What ties it to a page is
 * the CLOCK - a turn happens on the protocol page and nowhere else, because
 * `page-mismatch` refuses a message sent from any other one (§9.5) - so the
 * visit whose window contains the turn is the visit it belongs to.
 *
 * `leftAt === null` is the page the session is standing on, so its window has
 * no end and every later turn falls inside it.
 */
export function turnsOfVisit(
  page: SessionWalkPage,
  turns: readonly SessionTurn[],
): readonly SessionTurn[] {
  const entered = Date.parse(page.enteredAt);
  const left = page.leftAt === null ? Number.POSITIVE_INFINITY : Date.parse(page.leftAt);
  if (Number.isNaN(entered) || Number.isNaN(left)) return [];
  return turns.filter((turn) => {
    const at = Date.parse(turn.createdAt);
    return !Number.isNaN(at) && at >= entered && at <= left;
  });
}

/**
 * The index of the visit the protocol block is drawn ON, or null when no visit
 * holds a turn.
 *
 * ONE block and not one per visit, and that is the decision worth naming: the
 * chain of §12.2 is the PROTOCOL's - `stepRuns` draws it in protocol order and
 * annotates it with what the whole session did to it - so a second block would
 * draw one chain twice and claim two readings of it. A second visit to the same
 * page still appears in the walk, as the ordinary card it is: « une page entrée
 * deux fois apparaît deux fois » is about the traversal, and the traversal is
 * exactly what an ordinary card shows.
 *
 * Null with turns in hand is not impossible - a visit row lost, a clock that
 * disagrees with itself - and the walk answers it by drawing the block at the
 * end rather than by dropping the only reading of the conversation.
 */
export function protocolVisitIndex(detail: SessionDetail): number | null {
  if (detail.turns.length === 0) return null;
  const found = detail.pages.findIndex((page) => turnsOfVisit(page, detail.turns).length > 0);
  return found < 0 ? null : found;
}
